import { useEffect, useRef, useCallback } from 'react';

/**
 * useSyncRefresh — Keeps data fresh across mobile/web/tabs.
 *
 * Triggers a refetch when:
 *  1. Tab/app becomes visible again (visibilitychange)
 *  2. Window regains focus (covers mobile app-switch)
 *  3. Network comes back online
 *  4. Optional polling interval
 *
 * Usage:
 *   useSyncRefresh(fetchData, { pollInterval: 30000 })
 */
export function useSyncRefresh(fetchFn, options) {
  var opts = options || {};
  var pollInterval = opts.pollInterval || 0;
  var enabled = opts.enabled !== false;
  var stableRef = useRef(fetchFn);
  stableRef.current = fetchFn;

  var lastFetchRef = useRef(Date.now());

  var doRefresh = useCallback(function () {
    // Debounce: skip if we fetched less than 2s ago
    if (Date.now() - lastFetchRef.current < 2000) return;
    lastFetchRef.current = Date.now();
    if (stableRef.current) stableRef.current();
  }, []);

  useEffect(function () {
    if (!enabled) return;

    // 1. Visibility change (tab switch, mobile app resume)
    function onVisibility() {
      if (!document.hidden) doRefresh();
    }

    // 2. Window focus (covers alt-tab, mobile task switcher)
    function onFocus() { doRefresh(); }

    // 3. Network reconnect
    function onOnline() { doRefresh(); }

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);

    // 4. Polling (if requested)
    var pollTimer = null;
    if (pollInterval > 0) {
      pollTimer = setInterval(function () {
        if (!document.hidden) doRefresh();
      }, pollInterval);
    }

    // 5. Cross-tab sync via BroadcastChannel
    var channel = null;
    try {
      channel = new BroadcastChannel('parentum-sync');
      channel.onmessage = function () { doRefresh(); };
    } catch (_) {}

    return function () {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      if (pollTimer) clearInterval(pollTimer);
      if (channel) channel.close();
    };
  }, [enabled, pollInterval, doRefresh]);
}

/**
 * notifySyncUpdate — Call after any mutation (create, update, delete)
 * to tell other tabs to refresh.
 */
export function notifySyncUpdate() {
  try {
    var channel = new BroadcastChannel('parentum-sync');
    channel.postMessage({ type: 'data-changed', ts: Date.now() });
    channel.close();
  } catch (_) {}
}
